import React, { forwardRef, useImperativeHandle } from 'react';
import classnames from 'classnames';
import measureLineHeight from 'line-height';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
} from 'signer-app/signature-request/constants';
import styles from 'signer-app/signature-request/display-field/text.module.css';
import { TextField } from 'signer-app/types/editor-types';
import { getMobileAdjustments } from 'signer-app/signer-signature-document/text-field-hooks';
import { useFeatureFlag } from 'signer-app/context/feature-flags';

const NOOP = () => {};

// These get spread into all display fields, but this one doesn't use them.
type UnusedProps = {
  height?: any;
  width?: any;
  textScale?: any;
  isActive?: any;
  selectField?: any;
  updateFields?: any;
  runZoomIntoField?: any;
};

type Props = UnusedProps & {
  fieldData: TextField;
  className?: string;
  disabled?: boolean;
  style?: React.StyleHTMLAttributes<HTMLTextAreaElement>;
  id?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  validationErrors?: any;
  isOverlay: boolean;
  zoomIntoField?: (field: TextField) => void;
};

export type TextAreaRef = {
  focus: (options?: FocusOptions) => void;
  measureLineHeight: () => number;
  textRef: React.RefObject<HTMLTextAreaElement>; // Expose textRef for useSignerTextInput
};

const TextArea = forwardRef<TextAreaRef, Props>((props, ref) => {
  const divRef = React.useRef<HTMLDivElement>(null);
  const textRef = React.useRef<HTMLTextAreaElement>(null);
  const shouldScaleTextField = useFeatureFlag(
    'sign_core_2025_11_20_text_field_scaling',
  );
  useImperativeHandle(ref, () => ({
    focus: (focusOptions?: FocusOptions) => {
      const target = props.disabled ? divRef : textRef;
      target.current?.focus(focusOptions);
    },
    measureLineHeight: () => {
      if (typeof jest !== 'undefined') {
        // JSDom can't measure the line height, so for tests I think fontSize is
        // good enough
        return props.fieldData.fontSize || DEFAULT_FONT_SIZE;
      }
      return measureLineHeight(textRef.current!);
    },
    textRef, // Expose the ref so parent can access the textarea element
  }));

  const onFocus = () => {
    const { zoomIntoField, fieldData } = props;

    // Only trigger on mobile
    if (zoomIntoField && window.innerWidth < 768) {
      zoomIntoField!(fieldData);
    }
  };

  const {
    fieldData,
    height,
    width,
    textScale,
    isActive,
    validationErrors,
    selectField,
    updateFields,
    zoomIntoField,
    runZoomIntoField,
    isOverlay,
    ...rest
  } = props;

  const fontFamily = fieldData.fontFamily || DEFAULT_FONT_FAMILY;
  const fontSize = fieldData.fontSize || DEFAULT_FONT_SIZE;
  const readOnly = fieldData.readOnly || false;
  const fontWeight =
    fieldData.fontWeight && fieldData.fontWeight >= 700 ? 'bold' : 'normal';
  const color = fieldData.fontColor;

  const mobileZoomStyle = React.useMemo(() => {
    const {
      fontSize: fontSizeToUse,
      scale,
      sizeScaleFactor,
    } = getMobileAdjustments(fontSize, shouldScaleTextField);

    return {
      fontSize: fontSizeToUse,
      transform: `scale(${scale})`,
      width: `${sizeScaleFactor * 100}%`,
      height: `${sizeScaleFactor * 100}%`,
    };
  }, [fontSize, shouldScaleTextField]);

  const className = classnames(
    props.className,
    styles.textarea,
    styles[`font--${fontFamily}`],
  );

  const style = {
    height: mobileZoomStyle.height,
    width: mobileZoomStyle.width,
    fontSize: mobileZoomStyle.fontSize,
    transform: mobileZoomStyle.transform,
    fontWeight,
    color,
    ...rest.style,
  };

  const value = fieldData.value || '';

  if (isOverlay && value === '') {
    return null;
  }

  return (
    <div
      ref={divRef}
      tabIndex={
        // Something in here must be able to be focused for tabbing between
        // fields to work. If the text area is editable, then it gets focus,
        // but if it's disabled then this div needs a tab index. The signer
        // app uses a readOnly flag where nothing in here should have a tab
        // index.
        rest.disabled && !readOnly ? 0 : -1
      }
      style={{ outline: 'none', height: '100%' }}
    >
      <textarea
        placeholder={fieldData.placeholder}
        onChange={NOOP}
        {...rest}
        onFocus={onFocus}
        ref={textRef}
        className={className}
        value={value}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="on"
        wrap="off"
        aria-invalid={validationErrors && !!validationErrors[fieldData.id]}
        readOnly={readOnly}
        required={fieldData.required}
        style={{
          ...style,
          color: style.color ?? undefined,
        }}
      />
    </div>
  );
});

TextArea.displayName = 'TextArea';

export default React.memo(TextArea);
